/**
 * Staff Service - API client for staff records, CSV import,
 * teacher lookups, class assignments, and portal credentials
 */
import api from '../utils/api';

const staffService = {
  listStaff: async (params = {}) => {
    try {
      return await api.get('/api/staff', { params });
    } catch (error) {
      console.error('Error fetching staff:', error);
      throw error;
    }
  },

  getStaff: async (staffId) => {
    try {
      return await api.get(`/api/staff/${staffId}`);
    } catch (error) {
      console.error('Error fetching staff member:', error);
      throw error;
    }
  },

  getMyProfile: async () => {
    try {
      return await api.get('/api/staff/me/profile');
    } catch (error) {
      console.error('Error fetching own staff profile:', error);
      throw error;
    }
  },

  createStaff: async (data) => {
    try {
      return await api.post('/api/staff', data);
    } catch (error) {
      console.error('Error creating staff member:', error);
      throw error;
    }
  },

  updateStaff: async (staffId, data) => {
    try {
      return await api.put(`/api/staff/${staffId}`, data);
    } catch (error) {
      console.error('Error updating staff member:', error);
      throw error;
    }
  },

  importStaff: async (formData) => {
    try {
      return await api.post('/api/staff/import', formData);
    } catch (error) {
      console.error('Error importing staff:', error);
      throw error;
    }
  },

  listTeachers: async () => {
    try {
      return await api.get('/api/staff/teachers');
    } catch (error) {
      console.error('Error fetching teachers:', error);
      throw error;
    }
  },

  getMyClasses: async () => {
    try {
      return await api.get('/api/staff/my-classes');
    } catch (error) {
      console.error('Error fetching my classes:', error);
      throw error;
    }
  },

  regeneratePortalCredentials: async (staffId) => {
    try {
      return await api.post(`/api/staff/${staffId}/portal-credentials`);
    } catch (error) {
      console.error('Error regenerating staff portal credentials:', error);
      throw error;
    }
  },

  createAssignment: async (staffId, data) => {
    try {
      return await api.post(`/api/staff/${staffId}/assignments`, data);
    } catch (error) {
      console.error('Error creating staff assignment:', error);
      throw error;
    }
  },

  updateAssignment: async (staffId, assignmentId, data) => {
    try {
      return await api.patch(`/api/staff/${staffId}/assignments/${assignmentId}`, data);
    } catch (error) {
      console.error('Error updating staff assignment:', error);
      throw error;
    }
  },

  deleteAssignment: async (staffId, assignmentId) => {
    try {
      return await api.delete(`/api/staff/${staffId}/assignments/${assignmentId}`);
    } catch (error) {
      console.error('Error deleting staff assignment:', error);
      throw error;
    }
  },

  // ---- Payout settlement details (where money for staff-initiated paid
  // activities, e.g. extra classes, is sent via a Paystack subaccount) ----

  listPayoutBanks: async () => {
    try {
      return await api.get('/api/staff/banks/list');
    } catch (error) {
      console.error('Error fetching bank list:', error);
      throw error;
    }
  },

  getMyPayoutDetails: async () => {
    try {
      return await api.get('/api/staff/my-payout-details');
    } catch (error) {
      console.error('Error fetching payout details:', error);
      throw error;
    }
  },

  submitMyPayoutDetails: async (data) => {
    try {
      return await api.put('/api/staff/my-payout-details', data);
    } catch (error) {
      console.error('Error submitting payout details:', error);
      throw error;
    }
  },

  listPendingPayoutDetails: async () => {
    try {
      return await api.get('/api/staff/payout-details/pending');
    } catch (error) {
      console.error('Error fetching pending payout details:', error);
      throw error;
    }
  },

  getStaffPayoutDetails: async (staffId) => {
    try {
      return await api.get(`/api/staff/${staffId}/payout-details`);
    } catch (error) {
      console.error('Error fetching staff payout details:', error);
      throw error;
    }
  },

  verifyStaffPayoutDetails: async (staffId) => {
    try {
      return await api.post(`/api/staff/${staffId}/payout-details/verify`, {});
    } catch (error) {
      console.error('Error verifying payout details:', error);
      throw error;
    }
  },

  rejectStaffPayoutDetails: async (staffId, reason) => {
    try {
      return await api.post(`/api/staff/${staffId}/payout-details/reject`, { reason });
    } catch (error) {
      console.error('Error rejecting payout details:', error);
      throw error;
    }
  },

  // ---- Probation ----

  startProbation: async (staffId, endDate) => {
    try {
      return await api.put(`/api/staff/${staffId}/probation/start`, { probation_end_date: endDate });
    } catch (error) {
      console.error('Error starting staff probation:', error);
      throw error;
    }
  },

  decideProbation: async (staffId, decision, newEndDate = null) => {
    try {
      const body = { decision };
      if (newEndDate) body.new_probation_end_date = newEndDate;
      return await api.put(`/api/staff/${staffId}/probation/decide`, body);
    } catch (error) {
      console.error('Error deciding staff probation:', error);
      throw error;
    }
  },

  getUpcomingProbations: async (days = 30) => {
    try {
      return await api.get('/api/staff/probation/upcoming', { params: { days } });
    } catch (error) {
      console.error('Error fetching upcoming probation reviews:', error);
      throw error;
    }
  }
};

export default staffService;
